import qrcode from 'qrcode-generator';

/**
 * QR code du lien d'invitation (concept §6.2 : « affichage d'un QR code »).
 *
 * Le SVG est construit avec l'API DOM (pas d'`innerHTML`) : aucune chaîne
 * n'est interprétée comme du balisage, même si l'URL contenait des caractères
 * exotiques.
 */
const SVG_NS = 'http://www.w3.org/2000/svg';

export interface QrOptions {
  /** Marge autour du motif, en modules (4 = recommandation de la norme). */
  readonly margin?: number;
  /** Correction d'erreur : `L` suffit pour une URL courte affichée à l'écran. */
  readonly level?: 'L' | 'M' | 'Q' | 'H';
}

/** Construit un `<svg>` carré contenant le QR code de `text`. */
export function createQrSvg(text: string, options: QrOptions = {}): SVGSVGElement {
  const margin = options.margin ?? 2;
  const qr = qrcode(0, options.level ?? 'L');
  qr.addData(text);
  qr.make();

  const count = qr.getModuleCount();
  const size = count + margin * 2;

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('role', 'img');
  svg.setAttribute('shape-rendering', 'crispEdges');
  svg.setAttribute('aria-label', `QR code : ${text}`);

  const background = document.createElementNS(SVG_NS, 'rect');
  background.setAttribute('x', '0');
  background.setAttribute('y', '0');
  background.setAttribute('width', String(size));
  background.setAttribute('height', String(size));
  background.setAttribute('fill', '#ffffff');
  svg.append(background);

  // Un `<path>` unique plutôt que N rects : le DOM reste léger même en v10+.
  const commands: string[] = [];
  for (let row = 0; row < count; row += 1) {
    for (let col = 0; col < count; col += 1) {
      if (qr.isDark(row, col)) {
        commands.push(`M${col + margin} ${row + margin}h1v1h-1z`);
      }
    }
  }
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', commands.join(''));
  path.setAttribute('fill', '#000000');
  svg.append(path);

  return svg;
}

/** Lien d'invitation à une partie : `<origine>/?join=CODE`. */
export function joinUrl(code: string, origin: string = window.location.origin): string {
  return `${origin}/?join=${encodeURIComponent(code)}`;
}
